import { Alert, Button, Card, Input, Select, Space, Typography, message } from "antd";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { Cascader as ZhCascader, Dialog as ZhDialog, ZhMap } from "zhihao-ui-react";
import type { CascaderOption, ZhMapRef } from "zhihao-ui-react";

import { shippingRequestHttp } from "@/api";
import { CDN_URL } from "@/config";
import { useUserStore } from "@/stores/react/authStore";
import { validatePhone } from "@/utils/validate";
import { onAddressInput, sliceGbCode } from "../wharf";
import RequiredMark from "@/views/components/RequiredMark";

export interface OilSiteFormData {
    id?: string | number;
    name?: string;
    brand?: string;
    sitePhone?: string;
    phone?: string;
    quantity?: number;
    provinceCode?: string | number;
    cityCode?: string | number;
    regionCode?: string | number;
    provinceName?: string;
    cityName?: string;
    regionName?: string;
    address?: string;
    latLon?: string;
}

/** 旧 Vue areaLib 使用的行政区节点，保留 id/name/childs 级联字段契约。 */
export interface OilSiteAreaOption {
    id: string;
    name: string;
    shortName?: string;
    childs?: OilSiteAreaOption[];
}

/** 天地图联想项转换后的表单候选，兼容 Vue item.link 数据结构。 */
export interface OilSiteAddressSuggestion {
    value: string;
    address?: string;
    lonlat?: string;
    countyCode?: string;
}

export interface OilSiteMapSelection {
    areaCode: string[];
    address: string;
    latLon: string;
}

export interface OilSiteFormPageProps {
    loadSite?: (id: string) => Promise<OilSiteFormData>;
    createSite?: (data: OilSiteFormData) => Promise<unknown>;
    updateSite?: (id: string, data: OilSiteFormData) => Promise<unknown>;
    loadAreaOptions?: () => Promise<OilSiteAreaOption[]>;
    loadAddressSuggestions?: (keyword: string, regionCode?: string) => Promise<OilSiteAddressSuggestion[]>;
}

/** 复用 Vue 站点详情接口。 */
export async function loadOilSite(id: string): Promise<OilSiteFormData> {
    const response = await shippingRequestHttp.service.get<OilSiteFormData>(`/oil-site/${encodeURIComponent(id)}`);
    return response.data;
}

/** 复用 Vue 站点新增接口。 */
export async function createOilSite(data: OilSiteFormData) {
    const response = await shippingRequestHttp.service.post("/oil-site", data);
    return response.data;
}

/** 复用 Vue 站点编辑接口。 */
export async function updateOilSite(id: string, data: OilSiteFormData) {
    const response = await shippingRequestHttp.service.put(`/oil-site/${encodeURIComponent(id)}`, data);
    return response.data;
}

/** 延迟读取 Vue areaLib 的地区文件，避免首屏直接阻塞油站表单。 */
export async function loadOilSiteAreaOptions(): Promise<OilSiteAreaOption[]> {
    const response = await fetch(`${CDN_URL}map/areas.json`);
    if (!response.ok) throw new Error("区域数据加载失败");
    return (await response.json()) as OilSiteAreaOption[];
}

/** 将 Vue 天地图联想结果规范化为 React 表单候选。 */
export async function loadOilSiteAddressSuggestions(
    keyword: string,
    regionCode = ""
): Promise<OilSiteAddressSuggestion[]> {
    const result = (await onAddressInput(keyword, regionCode)) as
        | Array<{
              value?: string;
              link?: { address?: string; lonlat?: string; countyCode?: string; gbCode?: string };
          }>
        | undefined;
    return (result || []).map(item => ({
        value: item.value || item.link?.address || "",
        address: item.link?.address,
        lonlat: item.link?.lonlat,
        countyCode: item.link?.countyCode || item.link?.gbCode
    }));
}

const initialForm: OilSiteFormData = { quantity: 2 };

/** 从旧 Vue 级联节点中读取省、市、区简称，确保提交 payload 保留名称字段。 */
function resolveAreaNames(options: OilSiteAreaOption[], codes: string[]) {
    let current = options;
    const selected: Array<OilSiteAreaOption | undefined> = [];
    for (const code of codes) {
        const node = current.find(item => String(item.id) === code);
        selected.push(node);
        current = node?.childs || [];
    }
    return {
        provinceName: selected[0]?.shortName || selected[0]?.name || "",
        cityName: selected[1]?.shortName || selected[1]?.name || "",
        regionName: selected[2]?.shortName || selected[2]?.name || selected[1]?.shortName || selected[1]?.name || ""
    };
}

/** 将 Vue 地图经纬度文本转为 zhihao-ui-react 地图中心点。 */
function parseMapCenter(latLon?: string): [number, number] | undefined {
    const coordinates = latLon?.split(",").map(value => Number(value.trim()));
    if (!coordinates || coordinates.length < 2 || coordinates.some(value => !Number.isFinite(value))) return undefined;
    return [coordinates[0], coordinates[1]];
}

/** 复刻 Vue 地图拖动后的六位小数经纬度格式。 */
function formatMapCenter(center?: readonly number[]) {
    if (!center || center.length < 2) return "";
    return `${center[0].toFixed(6)}, ${center[1].toFixed(6)}`;
}

interface OilSiteMapDialogProps {
    open: boolean;
    areaOptions: OilSiteAreaOption[];
    areaLoading: boolean;
    initialAreaCode: string[];
    initialAddress: string;
    initialLatLon: string;
    loadAddressSuggestions: (keyword: string, regionCode?: string) => Promise<OilSiteAddressSuggestion[]>;
    ensureAreaOptions: () => void;
    onClose: () => void;
    onSubmit: (selection: OilSiteMapSelection) => void;
}

/** 油站专用地图弹窗，对齐旧 Vue MapDialog 的级联、联想、拖动和确定行为。 */
function OilSiteMapDialog({
    open,
    areaOptions,
    areaLoading,
    initialAreaCode,
    initialAddress,
    initialLatLon,
    loadAddressSuggestions,
    ensureAreaOptions,
    onClose,
    onSubmit
}: OilSiteMapDialogProps) {
    const [areaCode, setAreaCode] = useState<string[]>([]);
    const [address, setAddress] = useState("");
    const [latLon, setLatLon] = useState("");
    const [suggestions, setSuggestions] = useState<OilSiteAddressSuggestion[]>([]);
    const mapRef = useRef<ZhMapRef>(null);
    const addressTouchedRef = useRef(false);

    useEffect(() => {
        if (!open) return;
        setAreaCode(initialAreaCode);
        setAddress(initialAddress);
        setLatLon(initialLatLon);
        setSuggestions([]);
        addressTouchedRef.current = false;
    }, [initialAddress, initialAreaCode, initialLatLon, open]);

    useEffect(() => {
        if (!open || !addressTouchedRef.current || !address.trim()) {
            setSuggestions([]);
            return;
        }
        let disposed = false;
        const timer = window.setTimeout(() => {
            void loadAddressSuggestions(address.trim(), areaCode[2] || "")
                .then(result => {
                    if (!disposed) setSuggestions(result || []);
                })
                .catch(() => {
                    if (!disposed) setSuggestions([]);
                });
        }, 100);
        return () => {
            disposed = true;
            window.clearTimeout(timer);
        };
    }, [address, areaCode, loadAddressSuggestions, open]);

    const changeArea = (value: unknown) => {
        const nextArea = Array.isArray(value) && !Array.isArray(value[0]) ? value.map(String) : [];
        setAreaCode(nextArea);
        setAddress("");
        setLatLon("");
        setSuggestions([]);
        addressTouchedRef.current = false;
    };

    const selectSuggestion = (suggestion: OilSiteAddressSuggestion) => {
        const nextArea = areaCode.length ? areaCode : suggestion.countyCode ? sliceGbCode(suggestion.countyCode) : [];
        setAreaCode(nextArea);
        setAddress(suggestion.address || suggestion.value);
        setLatLon(suggestion.lonlat || "");
        setSuggestions([]);
        addressTouchedRef.current = false;
        const center = parseMapCenter(suggestion.lonlat);
        if (center) mapRef.current?.setCenter(center[0], center[1]);
    };

    const moveMap = () => setLatLon(formatMapCenter(mapRef.current?.getCenter()));
    const cascaderOptions = areaOptions as unknown as CascaderOption[];

    return (
        <ZhDialog
            open={open}
            title="位置"
            width={920}
            closeOnClickModal={false}
            destroyOnClose
            showClose
            onCancel={onClose}
            onOpen={ensureAreaOptions}
            footer={
                <Space>
                    <Button onClick={onClose}>取消</Button>
                    <Button type="primary" onClick={() => onSubmit({ areaCode, address, latLon })}>
                        确定
                    </Button>
                </Space>
            }
        >
            <div className="grid grid-cols-4 gap-2">
                <ZhCascader
                    aria-label="地图区域"
                    className="col-span-1"
                    loading={areaLoading}
                    options={cascaderOptions}
                    value={areaCode}
                    filterable
                    placeholder="请选择"
                    props={{ value: "id", label: "name", children: "childs" }}
                    onFocus={ensureAreaOptions}
                    onChange={changeArea}
                />
                <div className="col-span-2">
                    <Input
                        aria-label="详细地址"
                        maxLength={200}
                        placeholder="请输入详细地址"
                        value={address}
                        onChange={event => {
                            addressTouchedRef.current = true;
                            setAddress(event.target.value);
                        }}
                    />
                    {suggestions.length ? (
                        <div aria-label="地图地址联想结果" className="mt-1 flex flex-col gap-1">
                            {suggestions.map((suggestion, index) => (
                                <Button
                                    key={`${suggestion.value}-${index}`}
                                    type="link"
                                    onClick={() => selectSuggestion(suggestion)}
                                >
                                    {suggestion.value}
                                    {suggestion.address && suggestion.address !== suggestion.value
                                        ? ` ${suggestion.address}`
                                        : ""}
                                </Button>
                            ))}
                        </div>
                    ) : null}
                </div>
                {latLon ? <div className="col-span-1 self-center">经纬度 {latLon}</div> : null}
                <div className="relative col-span-4 min-h-96">
                    <ZhMap
                        ref={mapRef}
                        center={parseMapCenter(latLon)}
                        layerType="satellite"
                        copyrightImgUrl={`${CDN_URL}map/tdt-copyright-h-w.svg`}
                        className="h-96 w-full"
                        mapMoveEnd={moveMap}
                    />
                    <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
                        <span aria-label="地图中心坐标" className="rounded bg-white/90 px-2 py-1 shadow">
                            {latLon || "请选择地图位置"}
                        </span>
                    </div>
                </div>
            </div>
        </ZhDialog>
    );
}

/** 加油站点新增/编辑表单，保留 Vue 字段、区域级联、地址联想和地图选点行为。 */
export function OilSiteFormPage({
    loadSite = loadOilSite,
    createSite = createOilSite,
    updateSite = updateOilSite,
    loadAreaOptions = loadOilSiteAreaOptions,
    loadAddressSuggestions = loadOilSiteAddressSuggestions
}: OilSiteFormPageProps) {
    const location = useLocation();
    const { id: routeId } = useParams();
    const isEdit = location.pathname.includes("oilSiteEdit");
    const id = routeId || location.pathname.split("/").filter(Boolean).pop() || "";
    const navigate = useNavigate();
    // 对齐 Vue userStore.banButton：全局按钮封禁时保留提交入口但禁止提交。
    const banButton = useUserStore(state => state.banButton);
    const [form, setForm] = useState<OilSiteFormData>(initialForm);
    const [errorMessage, setErrorMessage] = useState("");
    const [areaOptions, setAreaOptions] = useState<OilSiteAreaOption[]>([]);
    const [areaLoading, setAreaLoading] = useState(false);
    const [areaLoaded, setAreaLoaded] = useState(false);
    const [addressSuggestions, setAddressSuggestions] = useState<OilSiteAddressSuggestion[]>([]);
    const [mapOpen, setMapOpen] = useState(false);
    const addressTouchedRef = useRef(false);

    useEffect(() => {
        if (!isEdit || !id) return;
        let disposed = false;
        void loadSite(id)
            .then(data => {
                if (!disposed) setForm(current => ({ ...current, ...data }));
            })
            .catch(() => {
                if (!disposed) setErrorMessage("站点回显失败");
            });
        return () => {
            disposed = true;
        };
    }, [id, isEdit, loadSite]);

    const ensureAreaOptions = async () => {
        if (areaLoaded || areaLoading) return;
        setAreaLoading(true);
        try {
            setAreaOptions((await loadAreaOptions()) || []);
            setAreaLoaded(true);
        } catch {
            setErrorMessage("区域数据加载失败");
        } finally {
            setAreaLoading(false);
        }
    };

    useEffect(() => {
        const address = form.address?.trim() || "";
        if (!addressTouchedRef.current || !address) {
            setAddressSuggestions([]);
            return;
        }
        let disposed = false;
        const timer = window.setTimeout(() => {
            void loadAddressSuggestions(address, String(form.regionCode || ""))
                .then(result => {
                    if (!disposed) setAddressSuggestions(result || []);
                })
                .catch(() => {
                    if (!disposed) setAddressSuggestions([]);
                });
        }, 100);
        return () => {
            disposed = true;
            window.clearTimeout(timer);
        };
    }, [form.address, form.regionCode, loadAddressSuggestions]);

    const setField = <K extends keyof OilSiteFormData>(key: K, value: OilSiteFormData[K]) => {
        setForm(current => ({ ...current, [key]: value }));
    };

    const changeArea = (value: unknown) => {
        const codes = Array.isArray(value) && !Array.isArray(value[0]) ? value.map(String) : [];
        const names = resolveAreaNames(areaOptions, codes);
        setForm(current => ({
            ...current,
            provinceCode: codes[0],
            cityCode: codes[1],
            regionCode: codes[2],
            ...names,
            address: "",
            latLon: ""
        }));
        addressTouchedRef.current = false;
        setAddressSuggestions([]);
    };

    const selectAddressSuggestion = (suggestion: OilSiteAddressSuggestion) => {
        const codes = form.regionCode
            ? [String(form.provinceCode || ""), String(form.cityCode || ""), String(form.regionCode)]
            : suggestion.countyCode
              ? sliceGbCode(suggestion.countyCode)
              : [];
        const names = resolveAreaNames(areaOptions, codes);
        setForm(current => ({
            ...current,
            address: suggestion.address || suggestion.value,
            latLon: suggestion.lonlat || "",
            ...(current.regionCode
                ? {}
                : { provinceCode: codes[0], cityCode: codes[1], regionCode: codes[2], ...names })
        }));
        addressTouchedRef.current = false;
        setAddressSuggestions([]);
    };

    /** 保留 Vue 表单的字段边界和错误文案，再执行同一接口 payload 提交。 */
    const validate = () => {
        if (!form.name?.trim()) return "请输入站点名称";
        if (form.name.trim().length > 32) return "不超过32位字符";
        if (!form.brand?.trim()) return "请输入品牌";
        if (form.brand.trim().length > 10) return "品牌最多10个字符";
        if (!validatePhone(form.sitePhone || "")) return "请输入正确的手机号";
        if (!validatePhone(form.phone || "")) return "请输入正确的手机号";
        if (!form.quantity || form.quantity < 1 || form.quantity > 5) return "请选择价格*数量";
        if (!form.provinceCode) return "请选择省份";
        if (!form.cityCode) return "请选择地级市";
        if (!form.regionCode) return "请选择县区";
        if (!form.address?.trim()) return "请输入详细地址";
        if (!form.latLon?.trim()) return "请选择经纬度";
        return "";
    };

    const submit = async () => {
        const validationError = validate();
        if (validationError) {
            setErrorMessage(validationError);
            return;
        }
        try {
            if (isEdit) await updateSite(id, form);
            else await createSite(form);
            message.success(isEdit ? "修改成功" : "添加成功");
            navigate(-1);
        } catch {
            setErrorMessage(isEdit ? "站点修改失败" : "站点创建失败");
        }
    };

    const handleMapSubmit = ({ areaCode, address, latLon }: OilSiteMapSelection) => {
        const names = resolveAreaNames(areaOptions, areaCode);
        setForm(current => ({
            ...current,
            provinceCode: areaCode[0],
            cityCode: areaCode[1],
            regionCode: areaCode[2],
            ...names,
            address,
            latLon
        }));
        addressTouchedRef.current = false;
        setAddressSuggestions([]);
        setMapOpen(false);
    };

    // 经纬度输入框和右侧图标都复用 Vue 的地图弹窗入口。
    // 打开地图前预加载级联数据，保证直接进入地图时也能沿用 Vue 的区域选择能力。
    const openMap = () => {
        void ensureAreaOptions();
        setMapOpen(true);
    };

    const textField = (
        label: string,
        key: keyof OilSiteFormData,
        options: { maxLength?: number; className?: string; placeholder?: string } = {}
    ): ReactNode => (
        <label className={options.className}>
            <span>
                <RequiredMark />
                {label}
            </span>
            <Input
                aria-label={label}
                maxLength={options.maxLength}
                placeholder={options.placeholder}
                value={(form[key] as string | number | undefined) ?? ""}
                onChange={event => {
                    if (key === "address") addressTouchedRef.current = true;
                    setField(key, event.target.value as never);
                }}
            />
        </label>
    );

    const areaCode = [form.provinceCode, form.cityCode, form.regionCode].filter(
        (value): value is string | number => value !== undefined && value !== ""
    );
    const cascaderOptions = areaOptions as unknown as CascaderOption[];

    return (
        <section aria-label="oil-site-form-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Typography.Title className="mb-4 mt-0" level={4}>
                    {isEdit ? "修改站点" : "新增站点"}
                </Typography.Title>
                {errorMessage ? <Alert message={errorMessage} role="alert" type="error" /> : null}
                <form
                    onSubmit={event => {
                        event.preventDefault();
                        void submit();
                    }}
                >
                    <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                        {textField("站点名称", "name", { maxLength: 32, placeholder: "请输入站点名称" })}
                        {textField("品牌", "brand", { maxLength: 10, placeholder: "请输入品牌" })}
                        {textField("站点联系方式", "sitePhone", { placeholder: "请输入站点联系方式" })}
                        {textField("维护人联系方式", "phone", { placeholder: "维护人联系方式" })}
                        <label title="设置展示在链云船务展示油品实际售卖价格的*数量，1-5为个、十、百、千、万">
                            <span>
                                <RequiredMark />
                                价格*数量
                            </span>
                            <Select
                                aria-label="价格*数量"
                                className="w-full"
                                value={form.quantity}
                                placeholder="请选择价格*数量"
                                options={[1, 2, 3, 4, 5].map(value => ({ value, label: value }))}
                                onChange={value => setField("quantity", value)}
                            />
                        </label>
                        <label className="md:col-span-1">
                            <span>
                                <RequiredMark />
                                地址
                            </span>
                            <ZhCascader
                                aria-label="地址"
                                className="w-full"
                                loading={areaLoading}
                                options={cascaderOptions}
                                value={areaCode}
                                filterable
                                placeholder="请选择"
                                props={{ value: "id", label: "name", children: "childs" }}
                                onFocus={() => void ensureAreaOptions()}
                                onChange={changeArea}
                            />
                        </label>
                        {textField("详细地址", "address", {
                            className: "md:col-span-2",
                            maxLength: 200,
                            placeholder: "请输入详细地址"
                        })}
                        {addressSuggestions.length ? (
                            <div aria-label="地址联想结果" className="md:col-span-2 -mt-3 flex flex-col gap-1">
                                {addressSuggestions.map((suggestion, index) => (
                                    <Button
                                        key={`${suggestion.value}-${index}`}
                                        type="link"
                                        onClick={() => selectAddressSuggestion(suggestion)}
                                    >
                                        {suggestion.value}
                                        {suggestion.address && suggestion.address !== suggestion.value
                                            ? ` ${suggestion.address}`
                                            : ""}
                                    </Button>
                                ))}
                            </div>
                        ) : null}
                        <label className="md:col-span-1">
                            <span>
                                <RequiredMark />
                                经纬度
                            </span>
                            <Space.Compact className="w-full">
                                <Input
                                    aria-label="经纬度"
                                    placeholder="请选择经纬度"
                                    readOnly
                                    value={form.latLon || ""}
                                    onClick={openMap}
                                />
                                <Button aria-label="地图选点" onClick={openMap}>
                                    地图选点
                                </Button>
                            </Space.Compact>
                        </label>
                    </div>
                    <Space className="operation-fixed-btn mt-6">
                        <Button aria-label="提交" disabled={banButton} htmlType="submit" type="primary">
                            提交
                        </Button>
                        <Button aria-label="返回" onClick={() => navigate(-1)}>
                            返回
                        </Button>
                    </Space>
                </form>
            </Card>
            <OilSiteMapDialog
                open={mapOpen}
                areaOptions={areaOptions}
                areaLoading={areaLoading}
                initialAreaCode={areaCode.map(String)}
                initialAddress={form.address || ""}
                initialLatLon={form.latLon || ""}
                loadAddressSuggestions={loadAddressSuggestions}
                ensureAreaOptions={() => void ensureAreaOptions()}
                onClose={() => setMapOpen(false)}
                onSubmit={handleMapSubmit}
            />
        </section>
    );
}

export default OilSiteFormPage;
